import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Check, Zap, TrendingUp, CreditCard, Loader2, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SEO } from "@/components/seo";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useState, useMemo } from "react";

type SubscriptionPlan = {
  id: number;
  name: string;
  description: string;
  price: string;
  tier: string;
  features: string[] | null;
  products: string[];
  isActive: boolean;
  sortOrder: number;
  stripePriceId: string | null;
};

const TIER_META: Record<string, { icon: typeof Zap; label: string; features: string[] }> = {
  indicators: {
    icon: TrendingUp,
    label: "Indicators Only",
    features: [
      "All Sierra Chart custom indicators",
      "VWAP bands & session statistics",
      "Cumulative delta & market internals",
      "Instant download after subscribe",
      "Updates included",
    ],
  },
  indicators_orderflow: {
    icon: Zap,
    label: "Indicators + Order Flow",
    features: [
      "Everything in Indicators",
      "Full order flow suite",
      "Buy & sell signal studies",
      "Footprint & absorbed volume tools",
      "Priority support",
    ],
  },
};

export default function PricingPage() {
  const { user } = useAuth();
  const { toast } = useToast();

  // Read ?billing= from URL to allow home page to pass the selection through
  const initialBilling = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const b = params.get("billing");
    return b === "monthly" ? "monthly" : "yearly";
  }, []);

  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "yearly">(initialBilling);
  const [loadingPlanId, setLoadingPlanId] = useState<number | null>(null);

  const { data: plans, isLoading } = useQuery<SubscriptionPlan[]>({
    queryKey: ["/api/subscription-plans"],
  });

  const { data: currentSub } = useQuery<any>({
    queryKey: ["/api/subscriptions/my"],
    enabled: !!user,
  });

  async function handleSubscribe(planId: number) {
    if (!user) {
      window.location.href = `/auth?redirect=/pricing?billing=${billingPeriod}`;
      return;
    }
    setLoadingPlanId(planId);
    try {
      const res = await apiRequest("POST", "/api/subscriptions/create-checkout", {
        planId,
        billingPeriod,
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast({ title: "Error", description: data.message || "Failed to start checkout", variant: "destructive" });
      }
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Failed to start checkout", variant: "destructive" });
    } finally {
      setLoadingPlanId(null);
    }
  }

  const activePlans = plans?.filter((p) => p.isActive) ?? [];

  function displayPrice(monthlyPrice: string) {
    const m = Number(monthlyPrice);
    if (billingPeriod === "yearly") return (m * 0.8).toFixed(2);
    return m.toFixed(2);
  }

  return (
    <div className="min-h-screen">
      <SEO
        title="Subscription Plans"
        description="Subscribe to SierraChartPro for access to premium Sierra Chart indicators, templates, and buy & sell signals. Plans starting at $8.99/month."
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold mb-4">
            Choose Your Plan
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-8">
            Get unlimited access to professional Sierra Chart tools. Subscribe once and download everything included in your plan — new updates always included.
          </p>

          {/* Billing toggle */}
          <div className="inline-flex items-center gap-1 p-1 rounded-full border bg-muted/40">
            <button
              onClick={() => setBillingPeriod("monthly")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                billingPeriod === "monthly"
                  ? "bg-background shadow text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod("yearly")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                billingPeriod === "yearly"
                  ? "bg-background shadow text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Yearly
              <span className="bg-green-500 text-white text-xs font-semibold px-1.5 py-0.5 rounded-full leading-none">
                20% off
              </span>
            </button>
          </div>

          {billingPeriod === "yearly" && (
            <p className="text-sm text-muted-foreground mt-3">
              Billed as one annual payment · Cancel anytime
            </p>
          )}
        </div>

        {/* Active subscription notice */}
        {currentSub && (
          <div className="mb-8 p-4 rounded-lg border border-primary/30 bg-primary/5 text-center">
            <p className="text-sm font-medium">
              You already have an active subscription to <strong>{currentSub.plan?.name}</strong>.{" "}
              <Link href="/purchases">
                <span className="text-primary underline cursor-pointer">View your downloads →</span>
              </Link>
            </p>
          </div>
        )}

        {/* Plans */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2].map((i) => (
              <Skeleton key={i} className="h-96 w-full rounded-xl" />
            ))}
          </div>
        ) : activePlans.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            No subscription plans are currently available. Check back soon.
          </div>
        ) : (
          <div className={`grid gap-6 ${activePlans.length === 1 ? "max-w-md mx-auto" : "grid-cols-1 md:grid-cols-2"}`}>
            {activePlans.map((plan, idx) => {
              const meta = TIER_META[plan.tier] ?? TIER_META["indicators"];
              const Icon = meta.icon;
              const isHighlighted = idx === activePlans.length - 1 && activePlans.length > 1;
              const isCurrentPlan = currentSub?.planId === plan.id;
              const shownPrice = displayPrice(plan.price);
              const isLoading = loadingPlanId === plan.id;

              return (
                <Card
                  key={plan.id}
                  className={`relative overflow-hidden ${isHighlighted ? "border-primary shadow-lg ring-1 ring-primary" : ""}`}
                >
                  {isHighlighted && (
                    <div className="absolute top-0 right-0 left-0 h-1 bg-primary" />
                  )}
                  <CardContent className="p-6 flex flex-col h-full">
                    {isHighlighted && (
                      <Badge className="self-start mb-3">Most Popular</Badge>
                    )}

                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h2 className="font-bold text-lg leading-tight">{plan.name}</h2>
                        <p className="text-xs text-muted-foreground">{meta.label}</p>
                      </div>
                    </div>

                    <div className="mb-1">
                      <span className="text-4xl font-bold text-primary">
                        ${shownPrice}
                      </span>
                      <span className="text-muted-foreground">/month</span>
                    </div>

                    {billingPeriod === "yearly" && (
                      <p className="text-xs text-muted-foreground mb-4">
                        <span className="line-through mr-1">${Number(plan.price).toFixed(2)}/mo</span>
                        · billed ${(Number(plan.price) * 12 * 0.8).toFixed(2)}/year
                      </p>
                    )}

                    {plan.products && plan.products.length > 0 && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button type="button" className="text-xs text-primary underline underline-offset-2 mb-3 hover:text-primary/80 flex items-center gap-1">
                            <Package className="w-3 h-3" />
                            {plan.products.length} product{plan.products.length !== 1 ? "s" : ""} included
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-64 p-3">
                          <p className="text-xs font-semibold mb-2 text-foreground">Included in this plan</p>
                          <ul className="space-y-1.5">
                            {plan.products.map((name) => (
                              <li key={name} className="text-xs flex items-center gap-2 text-muted-foreground">
                                <Package className="w-3 h-3 text-primary flex-shrink-0" />
                                {name}
                              </li>
                            ))}
                          </ul>
                        </PopoverContent>
                      </Popover>
                    )}

                    {plan.description && (
                      <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
                        {plan.description}
                      </p>
                    )}

                    <ul className="space-y-2.5 mb-6 flex-1">
                      {((plan.features && plan.features.length > 0) ? plan.features : meta.features).map((feature) => (
                        <li key={feature} className="flex items-start gap-2 text-sm">
                          <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {isCurrentPlan ? (
                      <Button variant="outline" disabled className="w-full">
                        <Check className="w-4 h-4 mr-2" />
                        Current Plan
                      </Button>
                    ) : (
                      <Button
                        className="w-full"
                        variant={isHighlighted ? "default" : "outline"}
                        onClick={() => handleSubscribe(plan.id)}
                        disabled={!!currentSub || !!loadingPlanId}
                      >
                        {isLoading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <CreditCard className="w-4 h-4 mr-2" />
                        )}
                        Subscribe — ${shownPrice}/mo
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Footer note */}
        <p className="text-center text-xs text-muted-foreground mt-8">
          {billingPeriod === "yearly"
            ? "Billed annually at 20% off · Cancel anytime · Secure payments via Stripe"
            : "Billed monthly · Cancel anytime · Secure payments via Stripe"}
        </p>
      </div>
    </div>
  );
}
